<script lang="ts">
  import PronounPill from '#lib/ui/primitives/PronounPill.svelte';
  import UserSupporterBadge from '#lib/supporter/UserSupporterBadge.svelte';
  import { profileSupporterAppearance } from '#lib/supporter/variants.js';
  import { SUPPORTER_BADGE_FIELD } from '#lib/profile/fields.js';
  import type {
    MemberView,
    ProfileView,
    RoomPermissionsView,
    MutualRoomView,
    ProfileFieldView,
    PerMessageProfileView,
  } from '#src/generated/protocol';
  import IconContext from 'phosphor-svelte/lib/IconContext';
  import ArrowSquareOutIcon from 'phosphor-svelte/lib/ArrowSquareOutIcon';
  import CaretRightIcon from 'phosphor-svelte/lib/CaretRightIcon';
  import ChatCircleIcon from 'phosphor-svelte/lib/ChatCircleIcon';
  import PencilSimpleIcon from 'phosphor-svelte/lib/PencilSimpleIcon';
  import ChatsIcon from 'phosphor-svelte/lib/ChatsIcon';
  import ClockIcon from 'phosphor-svelte/lib/ClockIcon';
  import CopyIcon from 'phosphor-svelte/lib/CopyIcon';
  import HeartIcon from 'phosphor-svelte/lib/HeartIcon';
  import LockOpenIcon from 'phosphor-svelte/lib/LockOpenIcon';
  import DotsThreeIcon from 'phosphor-svelte/lib/DotsThreeIcon';
  import GavelIcon from 'phosphor-svelte/lib/GavelIcon';
  import SignOutIcon from 'phosphor-svelte/lib/SignOutIcon';
  import UserPlusIcon from 'phosphor-svelte/lib/UserPlusIcon';
  import PaperPlaneRightIcon from 'phosphor-svelte/lib/PaperPlaneRightIcon';
  import FlagIcon from 'phosphor-svelte/lib/FlagIcon';
  import ProhibitIcon from 'phosphor-svelte/lib/ProhibitIcon';
  import ShareNetworkIcon from 'phosphor-svelte/lib/ShareNetworkIcon';
  import ShieldIcon from 'phosphor-svelte/lib/ShieldIcon';
  import LockKeyIcon from 'phosphor-svelte/lib/LockKeyIcon';

  import { onDestroy } from 'svelte';

  import { goto } from '$app/navigation';
  import { resolve } from '$app/paths';
  import { roomSectionPath } from '#lib/rooms/permalink.js';
  import { useRoomCosmetics } from '#lib/rooms/room-cosmetics.svelte.js';
  import { hasDirectRoom, roomPathParamFromId, useRoomList } from '#lib/rooms/room-list.svelte.js';
  import { sendMessage } from '#lib/core/api/messages.svelte.js';
  import { useCoreClient } from '#lib/core/context.js';
  import { i18n } from '#lib/i18n.js';
  import { preferences } from '#lib/settings/preferences.svelte.js';
  import { toasts } from '#lib/ui/toasts.svelte.js';
  import { lastSeenBucket, lastSeenMs, usePresenceStore } from '#lib/rooms/presence.svelte.js';
  import { resolveUserStatus } from '#lib/rooms/user-status.js';
  import { profileOverrides } from '#lib/profile/profile-overrides.svelte.js';
  import ActionMenu from '#lib/ui/primitives/ActionMenu.svelte';
  import Avatar from '#lib/ui/primitives/Avatar.svelte';
  import ActionMenuItem from '#lib/ui/primitives/ActionMenuItem.svelte';
  import ActionMenuSeparator from '#lib/ui/primitives/ActionMenuSeparator.svelte';
  import ActionMenuSub from '#lib/ui/primitives/ActionMenuSub.svelte';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import ConfirmDialog from '#lib/ui/primitives/ConfirmDialog.svelte';
  import DialogFrame from '#lib/ui/primitives/DialogFrame.svelte';
  import FormField from '#lib/ui/primitives/FormField.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import Tooltip from '#lib/ui/primitives/Tooltip.svelte';
  import Pill from '#lib/ui/primitives/Pill.svelte';
  import ProfileCard from '#lib/ui/primitives/ProfileCard.svelte';
  import Skeleton from '#lib/ui/primitives/Skeleton.svelte';
  import TextInput from '#lib/ui/primitives/TextInput.svelte';

  import MessageReportDialog from '../messages/MessageReportDialog.svelte';
  import ProfileOverrideDialog from './ProfileOverrideDialog.svelte';
  import { sendReport } from '../messages/report';
  import FormattedBody from '../messages/FormattedBody.svelte';
  import type { MatrixLink } from '#lib/rooms/matrix-link.js';
  import { powerTag } from './power-tags.js';
  import RoleTagIcon from './RoleTagIcon.svelte';
  import { FOUNDER_POWER_LEVEL, type PowerLevelTagMap } from '../settings/power-level-tags.js';
  import { senderColor } from '../timeline/timeline-format';

  import '#lib/ui/primitives/menu.css';
  import CaretDownIcon from 'phosphor-svelte/lib/CaretDownIcon';
  import { profileFieldJson, profileFieldMap, profileFieldPreview } from './profile-field-map.js';
  import { MemberProfileRelations } from './member-profile-relations.svelte';
  import { MemberProfileActions, moderationErrorMessage } from './member-profile-actions';
  import UserSecurityDialog from './UserSecurityDialog.svelte';
  import PersonaCard from './PersonaCard.svelte';

  interface Props {
    userId: string;
    member: MemberView | null;
    roomId: string;
    ownPowerLevel?: number;
    permissions?: RoomPermissionsView | null;
    powerTags?: PowerLevelTagMap | null;
    profile: ProfileView | null;
    pmp?: PerMessageProfileView | null;
    onOpenAccount?: () => void;
    onNestedMenuOpenChange?: (open: boolean) => void;
    onAvatarClick?: (source: string, displayName: string) => void;
    onMatrixLink?: (link: MatrixLink, anchor: HTMLAnchorElement) => void;
    onPowerLevelChange?: (roomId: string, userId: string, level: number) => void;
    failed?: boolean;
    variant?: 'popover' | 'sheet';
  }

  let {
    userId,
    member,
    roomId,
    ownPowerLevel = 0,
    permissions = null,
    powerTags = null,
    profile,
    pmp = null,
    onOpenAccount,
    onNestedMenuOpenChange,
    onAvatarClick,
    onMatrixLink,
    onPowerLevelChange,
    failed = false,
    variant = 'popover',
  }: Props = $props();
  const core = useCoreClient();
  const roomCosmetics = useRoomCosmetics();
  const roomList = useRoomList();
  const presenceStore = usePresenceStore();
  let invitedMember = $state.raw<MemberView | null>(null);
  let membershipRequest = 0;

  $effect(() => {
    const request = ++membershipRequest;
    const targetRoomId = roomId;
    const targetUserId = userId;
    invitedMember = null;
    if (member !== null || targetRoomId === '' || !(permissions?.can_kick ?? false)) return;

    void core.rpc.members
      .list({ room_id: targetRoomId, memberships: ['invite'] })
      .then((members) => {
        if (request !== membershipRequest) return;
        invitedMember = members.find((entry) => entry.user_id === targetUserId) ?? null;
      })
      .catch((error: unknown) => {
        console.debug('[sable profile] invited member unavailable', error);
      });
  });

  let roomMember = $derived(member ?? invitedMember);
  let currentProfile = $derived(profile?.user_id === userId ? profile : null);
  let presence = $derived(presenceStore.get(userId));
  let presenceLabel = $derived(presence ? i18n.t(`presence.${presence.presence}`) : null);
  let userStatus = $derived(resolveUserStatus(currentProfile, presence));
  let lastSeenText = $derived.by(() => {
    if (!presence || presence.presence !== 'offline') return null;
    const ms = lastSeenMs(presence, Date.now());
    if (ms === null) return null;

    const bucket = lastSeenBucket(ms);
    switch (bucket.kind) {
      case 'now':
        return i18n.t('presence.lastSeenNow');
      case 'minutes':
        return i18n.t('presence.lastSeenMinutes', { count: bucket.count });
      case 'hours':
        return i18n.t('presence.lastSeenHours', { count: bucket.count });
      case 'days':
        return i18n.t('presence.lastSeenDays', { count: bucket.count });
    }
  });

  let ownIdentity = $derived({
    name: roomMember ? roomMember.display_name : (currentProfile?.display_name ?? null),
    avatar: roomMember ? roomMember.avatar_url : (currentProfile?.avatar_url ?? null),
  });
  let realName = $derived(ownIdentity.name ?? userId);
  let realAvatar = $derived(ownIdentity.avatar);
  let displayName = $derived(profileOverrides.name(userId, realName));
  let avatarUrl = $derived(profileOverrides.avatar(userId, realAvatar));
  let overrideColors = $derived(profileOverrides.colors(userId));
  let overrideOpen = $state(false);
  let reportOpen = $state(false);
  let securityOpen = $state(false);
  let color = $derived(currentProfile?.hero_color ?? senderColor(userId));
  let cosmetics = $derived(roomCosmetics?.for(userId) ?? null);
  let pronouns = $derived(
    cosmetics?.pronouns.length ? cosmetics.pronouns : (currentProfile?.pronouns ?? [])
  );
  let localTime = $derived.by(() => {
    const timezone = currentProfile?.timezone;
    if (!timezone) return null;

    try {
      const time = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
      }).format(new Date());
      return { time, timezone };
    } catch {
      return null;
    }
  });
  let animalText = $derived.by(() => {
    const animal = currentProfile?.animal;
    if (!animal) return null;

    let identity: string;
    if (animal.is_animal && animal.has_animal) {
      identity = i18n.t('timeline.animalBoth', { is: animal.is_animal, has: animal.has_animal });
    } else if (animal.is_animal) {
      identity = i18n.t('timeline.animalIs', { is: animal.is_animal });
    } else if (animal.has_animal) {
      identity = i18n.t('timeline.animalHas', { has: animal.has_animal });
    } else {
      return null;
    }

    const need = animal.animal_need ?? 'headpats';
    if (!need) return `${identity}!`;

    return i18n.t('timeline.animalNeed', { identity, need });
  });
  let extra = $derived(
    (currentProfile?.extra ?? []).filter((field) => field.key !== SUPPORTER_BADGE_FIELD)
  );
  let showFailure = $derived(failed && !currentProfile && roomMember === null);
  let profileLoading = $derived(!currentProfile && !failed);
  let isSelf = $derived(core.session?.user_id === userId);
  let profileColors = $derived(isSelf || preferences.renderProfileColors ? currentProfile : null);
  let canMessage = $derived(core.session !== null && !isSelf);
  let messageLabel = $derived(i18n.t('timeline.messageUser', { name: displayName }));
  let draft = $state('');
  let hasDraft = $derived(draft.trim() !== '');
  let sending = $state(false);
  let sendFailed = $state<'send' | 'open' | null>(null);
  let confirmingDirect = $state(false);
  let homeserver = $derived(userId.slice(userId.indexOf(':') + 1));
  let elevated = $derived(roomMember !== null && roomMember.power_level >= 50);
  let roleTag = $derived(
    roomMember && powerTags !== null ? powerTag(roomMember.power_level, i18n.t, powerTags) : null
  );
  let outranks = $derived(!isSelf && ownPowerLevel > (roomMember?.power_level ?? 0));
  let canKick = $derived(
    outranks &&
      (roomMember?.membership === 'join' || roomMember?.membership === 'invite') &&
      (permissions?.can_kick ?? false)
  );
  let canBan = $derived(
    outranks && roomMember?.membership !== 'ban' && (permissions?.can_ban ?? false)
  );
  let canInvite = $derived(
    !isSelf &&
      (roomMember === null || roomMember.membership === 'leave') &&
      (permissions?.can_invite ?? false)
  );
  let canUnban = $derived(
    !isSelf && roomMember?.membership === 'ban' && (permissions?.can_ban ?? false)
  );
  let canSetPower = $derived(
    (permissions?.can_change_power_levels ?? false) &&
      (isSelf
        ? ownPowerLevel < FOUNDER_POWER_LEVEL
        : ownPowerLevel > (roomMember?.power_level ?? 0))
  );
  let selfDemotion = $state<number | null>(null);
  // The spec caps what you may grant at your own level.
  let powerRoles = $derived(
    [...new Set([100, 50, 0, -1, ...Object.keys(powerTags ?? {}).map(Number)])]
      .filter(
        (level) =>
          level <= ownPowerLevel &&
          level !== FOUNDER_POWER_LEVEL &&
          level !== (roomMember?.power_level ?? 0)
      )
      .sort((left, right) => right - left)
      .map((level) => ({ level, label: powerTag(level, i18n.t, powerTags ?? {}).name }))
  );
  let profileLink = $derived(`https://matrix.to/#/${userId}`);
  const canShareLink = typeof navigator !== 'undefined' && 'share' in navigator;
  const relations = new MemberProfileRelations(core);
  const profileActions = new MemberProfileActions(core);
  let mutualRooms = $derived(relations.rooms);
  let ignored = $derived(relations.ignored);
  let miscOpen = $state(false);
  let sharedRooms = $derived(mutualRooms.filter((room) => !room.is_space));
  let sharedSpaces = $derived(mutualRooms.filter((room) => room.is_space));
  let sharedDirect = $derived(
    sharedRooms.filter((room) => roomList.byId(room.room_id)?.is_direct === true)
  );
  let sharedGroups = $derived(
    sharedRooms.filter((room) => roomList.byId(room.room_id)?.is_direct !== true)
  );
  let mutualLabel = $derived(i18n.t('timeline.profileMutualRooms', { count: mutualRooms.length }));
  let hasMeta = $derived(Boolean(localTime || animalText || roleTag || lastSeenText));
  let activeExtra = $state<ProfileFieldView | null>(null);

  $effect(() => {
    relations.sync(userId, isSelf);
  });

  onDestroy(() => relations.dispose());

  async function copyUserId(): Promise<void> {
    await profileActions.copy(userId);
  }

  async function copyProfileLink(): Promise<void> {
    await profileActions.copy(profileLink);
  }

  async function copyServer(): Promise<void> {
    await profileActions.copy(homeserver);
  }

  async function shareProfileLink(): Promise<void> {
    await profileActions.share(profileLink, displayName);
  }

  function openServer(): void {
    profileActions.openServer(homeserver);
  }

  async function toggleIgnored(): Promise<void> {
    const next = !ignored;
    try {
      relations.ignored = await profileActions.setIgnored(userId, next);
    } catch (error) {
      console.warn('[sable profile] could not change ignore state', error);
    }
  }

  function moderate(action: (roomId: string, userId: string) => Promise<unknown>): () => void {
    return () => {
      void action(roomId, userId).catch((error: unknown) => {
        console.warn('[sable profile] moderation action failed', error);
      });
    };
  }

  let moderationAction = $state<'kick' | 'ban' | null>(null);
  let moderationReason = $state('');
  let moderationBusy = $state(false);
  let moderationError = $state<string | null>(null);
  const moderationFieldId = $props.id();
  const miscId = `${moderationFieldId}-misc`;

  function openModeration(action: 'kick' | 'ban'): void {
    moderationAction = action;
    moderationReason = '';
    moderationError = null;
  }

  function cancelModeration(): void {
    moderationAction = null;
    moderationReason = '';
  }

  async function confirmModeration(): Promise<void> {
    const action = moderationAction;
    if (!action || moderationBusy) return;

    const reason = moderationReason.trim();
    moderationBusy = true;
    moderationError = null;
    try {
      await profileActions.moderate(roomId, userId, action, reason || null);
      moderationAction = null;
      moderationReason = '';
    } catch (error) {
      console.warn('[sable profile] moderation action failed', error);
      moderationError = moderationErrorMessage(error);
    } finally {
      moderationBusy = false;
    }
  }

  function setPowerLevel(level: number): void {
    if (isSelf && selfDemotion === null) {
      selfDemotion = level;
      return;
    }
    selfDemotion = null;
    const target = roomId;
    const user = userId;
    void profileActions.setPowerLevel(target, user, level).then(
      () => onPowerLevelChange?.(target, user, level),
      (error: unknown) => {
        console.warn('[sable profile] power level change failed', error);
        toasts.error(i18n.t('errors.actionFailed'));
      }
    );
  }

  function openRoom(target: string): void {
    // eslint-disable-next-line svelte/no-navigation-without-resolve -- roomSectionPath() resolves the route itself
    void goto(roomSectionPath(roomList.rooms, target));
  }

  async function openDirectMessage(body: string): Promise<void> {
    if (sending) return;

    let step: 'send' | 'open' = 'open';
    sending = true;
    sendFailed = null;
    try {
      const dmId = await core.rpc.rooms.createDm({ user_id: userId, encrypted: null });
      if (body) {
        step = 'send';
        await sendMessage(core.rpc, dmId, body);
        draft = '';
        step = 'open';
      }
      await goto(resolve('/(app)/direct/[roomId]', { roomId: roomPathParamFromId(dmId) }));
    } catch (error) {
      console.warn('[sable profile] could not open a chat', error);
      sendFailed = step;
    } finally {
      sending = false;
    }
  }

  function messageUser(): void {
    if (hasDirectRoom(roomList.rooms, userId)) void openDirectMessage('');
    else confirmingDirect = true;
  }

  function sendDirectMessage(event: SubmitEvent): void {
    event.preventDefault();
    void openDirectMessage(draft.trim());
  }
</script>

{#snippet pronounRow()}
  {#if pronouns.length > 0}
    <PronounPill class="profile-pronoun-pill" {pronouns} />
  {/if}
  {#if currentProfile?.supporter_awards}
    <UserSupporterBadge
      {userId}
      awards={currentProfile.supporter_awards}
      name={displayName}
      isOwnBadge={isSelf}
      class="profile-supporter-badge"
      {...profileSupporterAppearance(currentProfile.extra)}
    />
  {/if}
{/snippet}
{#snippet metaRow()}
  {#if lastSeenText}
    <span class="profile-meta-item">{lastSeenText}</span>
  {/if}
  {#if localTime}
    <span class="profile-meta-item">
      <ClockIcon />
      {localTime.time}
      <span class="profile-meta-aside">({localTime.timezone})</span>
    </span>
  {/if}
  {#if roleTag}
    <span class={['profile-meta-item', { 'profile-meta-elevated': elevated }]}>
      <ShieldIcon />
      {#if roleTag.icon}<RoleTagIcon icon={roleTag.icon} class="profile-role-icon" />{/if}
      {roleTag.name}
    </span>
  {/if}
  {#if animalText}
    <span class="profile-meta-item"><HeartIcon />{animalText}</span>
  {/if}
{/snippet}

{#snippet actionRow()}
  {#if pmp && onOpenAccount}
    <Pill onclick={onOpenAccount}>{i18n.t('timeline.openAccount')}</Pill>
  {:else}
    <ActionMenu label={i18n.t('common.share')} align="start" onOpenChange={onNestedMenuOpenChange}>
      {#snippet trigger({ props })}
        <Pill {...props}>
          <ShareNetworkIcon size={14} />
          {i18n.t('common.share')}
        </Pill>
      {/snippet}
      <IconContext values={{ 'aria-hidden': 'true' }}>
        <ActionMenuItem onSelect={copyUserId}>
          {i18n.t('timeline.profileCopyId')}
        </ActionMenuItem>
        <ActionMenuItem onSelect={copyProfileLink}>
          {i18n.t('timeline.profileCopyLink')}
        </ActionMenuItem>
      </IconContext>
    </ActionMenu>
  {/if}
  {#if !isSelf}
    <ActionMenu
      label={mutualLabel}
      class="profile-mutual-menu"
      align="start"
      onOpenChange={onNestedMenuOpenChange}
    >
      {#snippet trigger({ props })}
        <Pill {...props}>
          <ChatsIcon size={14} />
          {mutualLabel}
        </Pill>
      {/snippet}
      {#if sharedSpaces.length > 0}
        {@render mutualRows(sharedSpaces)}
      {/if}
      {#if sharedSpaces.length > 0 && sharedRooms.length > 0}
        <ActionMenuSeparator />
      {/if}
      {@render mutualRoomRows()}
    </ActionMenu>
  {/if}
  <ActionMenu label={i18n.t('timeline.moreActions')} onOpenChange={onNestedMenuOpenChange}>
    {#snippet trigger({ props })}
      <Pill {...props} iconOnly aria-label={i18n.t('timeline.moreActions')}>
        <DotsThreeIcon size={14} />
      </Pill>
    {/snippet}
    <IconContext values={{ 'aria-hidden': 'true' }}>
      <ActionMenuItem onSelect={copyServer}>
        <CopyIcon />
        {i18n.t('timeline.profileCopyServer')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={openServer}>
        <ArrowSquareOutIcon />
        {i18n.t('timeline.profileOpenServer')}
      </ActionMenuItem>
      {#if canShareLink}
        <ActionMenuItem onSelect={shareProfileLink}>
          <ShareNetworkIcon />
          {i18n.t('timeline.profileShareLink')}
        </ActionMenuItem>
      {/if}
      {#if !isSelf}
        <ActionMenuSeparator />
        <ActionMenuItem onSelect={() => (securityOpen = true)}>
          <LockKeyIcon />
          {i18n.t('timeline.profileEncryption')}
        </ActionMenuItem>
      {/if}
      {#if canInvite}
        <ActionMenuItem
          onSelect={moderate((room_id, user_id) => core.rpc.rooms.invite({ room_id, user_id }))}
        >
          <UserPlusIcon />
          {i18n.t('timeline.profileInvite')}
        </ActionMenuItem>
      {/if}
      {#if canUnban}
        <ActionMenuItem
          onSelect={moderate((room_id, user_id) =>
            core.rpc.members.unban({ room_id, user_id, reason: null })
          )}
        >
          <LockOpenIcon />
          {i18n.t('timeline.profileUnban')}
        </ActionMenuItem>
      {/if}
      {#if canSetPower}
        <ActionMenuSub label={i18n.t('timeline.profileChangePower')}>
          {#snippet trigger()}
            <ShieldIcon />
            {i18n.t('timeline.profileChangePower')}
          {/snippet}
          <IconContext values={{ 'aria-hidden': 'true' }}>
            {#each powerRoles as role (role.level)}
              <ActionMenuItem
                onSelect={() => {
                  setPowerLevel(role.level);
                }}
              >
                <span class="profile-power-name">{role.label}</span>
                <span class="profile-power-level">{role.level}</span>
              </ActionMenuItem>
            {/each}
          </IconContext>
        </ActionMenuSub>
      {/if}
      {#if canKick}
        <ActionMenuItem
          destructive
          onSelect={() => {
            openModeration('kick');
          }}
        >
          <SignOutIcon />
          {i18n.t('timeline.profileKick')}
        </ActionMenuItem>
      {/if}
      {#if canBan}
        <ActionMenuItem
          destructive
          onSelect={() => {
            openModeration('ban');
          }}
        >
          <GavelIcon />
          {i18n.t('timeline.profileBan')}
        </ActionMenuItem>
      {/if}
      {#if !isSelf}
        {#if canKick || canBan}
          <ActionMenuSeparator />
        {/if}
        <ActionMenuItem onSelect={() => (overrideOpen = true)}>
          <PencilSimpleIcon />
          {i18n.t('timeline.profileOverride')}
        </ActionMenuItem>
        <ActionMenuSeparator />
        <ActionMenuItem destructive onSelect={toggleIgnored}>
          <ProhibitIcon />
          {ignored ? i18n.t('timeline.profileUnblock') : i18n.t('timeline.profileBlock')}
        </ActionMenuItem>
        <ActionMenuItem destructive onSelect={() => (reportOpen = true)}>
          <FlagIcon />
          {i18n.t('timeline.profileReport')}
        </ActionMenuItem>
      {/if}
    </IconContext>
  </ActionMenu>
{/snippet}

{#snippet messageAction()}
  <Pill variant="primary" onclick={messageUser}>
    <ChatCircleIcon size={14} weight="fill" />
    {i18n.t('timeline.profileMessage')}
  </Pill>
{/snippet}

{#snippet profileBelow()}
  <div class="profile-card-actions">{@render actionRow()}</div>
{/snippet}

{#snippet metaPlaceholder()}
  <span class="profile-meta-item"
    ><Skeleton style="height: var(--font-size-small); width: 5rem" /></span
  >
  <span class="profile-meta-item"
    ><Skeleton style="height: var(--font-size-small); width: 7rem" /></span
  >
{/snippet}

{#snippet bioPanel()}
  {#if showFailure}
    <Alert variant="warning" role="status">{i18n.t('timeline.profileUnavailable')}</Alert>
  {:else if currentProfile?.bio}
    <FormattedBody
      html={currentProfile.bio}
      senderTimezone={currentProfile.timezone}
      keepMentionText
      {onMatrixLink}
    />
  {/if}
{/snippet}

{#snippet mutualRows(rooms: readonly MutualRoomView[])}
  {#each rooms as room (room.room_id)}
    <ActionMenuItem
      onSelect={() => {
        openRoom(room.room_id);
      }}
    >
      <Avatar
        id={room.room_id}
        src={roomList.byId(room.room_id)?.avatar_url}
        name={room.name ?? room.room_id}
        size="small"
      />
      <span class="profile-mutual-name">{room.name ?? room.room_id}</span>
    </ActionMenuItem>
  {/each}
{/snippet}

{#snippet mutualRoomRows()}
  {@render mutualRows(sharedGroups)}
  {#if sharedGroups.length > 0 && sharedDirect.length > 0}
    <ActionMenuSeparator />
  {/if}
  {@render mutualRows(sharedDirect)}
{/snippet}

{#snippet composer()}
  <form class="profile-composer" onsubmit={sendDirectMessage}>
    <TextInput
      bind:value={draft}
      class="profile-composer-input"
      placeholder={messageLabel}
      aria-label={messageLabel}
      disabled={sending}
    />
    <Tooltip label={i18n.t('timeline.openChat')} disabled={hasDraft}>
      {#snippet trigger({ props })}
        <IconButton
          {...props}
          label={hasDraft ? i18n.t('timeline.sendMessage') : i18n.t('timeline.openChat')}
          variant={hasDraft ? 'primary' : 'secondary'}
          size="small"
          type="submit"
          disabled={sending}
        >
          {#if hasDraft}
            <PaperPlaneRightIcon />
          {:else}
            <ChatCircleIcon />
          {/if}
        </IconButton>
      {/snippet}
    </Tooltip>
  </form>
  {#if sendFailed}
    <p class="profile-composer-error" role="status">
      {sendFailed === 'send' ? i18n.t('timeline.sendFailed') : i18n.t('timeline.openChatFailed')}
    </p>
  {/if}
{/snippet}

{#snippet miscData()}
  <Button
    class="profile-extra"
    aria-expanded={miscOpen}
    aria-controls={miscId}
    onclick={() => {
      miscOpen = !miscOpen;
      activeExtra = null;
    }}
    block
  >
    {#if miscOpen}
      <CaretDownIcon />
      {#if activeExtra}
        {activeExtra.key}
      {:else}
        {i18n.t('timeline.profileHideMiscData', { count: extra.length })}
      {/if}
    {:else}
      <CaretRightIcon />
      {i18n.t('timeline.profileMiscData', { count: extra.length })}
    {/if}
  </Button>
  {#if miscOpen}
    <div class="profile-extra-open" id={miscId}>
      {#if activeExtra === null}
        <div class="profile-keys">
          {#each extra as field (field.key)}
            <Button
              size="small"
              class="choice"
              block
              onclick={() => {
                activeExtra = field;
              }}
            >
              {field.key}
            </Button>
          {/each}
        </div>
      {:else if preferences.developerTools}
        <pre class="profile-extra-json">{profileFieldJson(activeExtra.value)}</pre>
      {:else}
        {@const value = profileFieldPreview(activeExtra.value)}
        {@const map = profileFieldMap(value)}
        {#if map}
          <table class="profile-key-table" aria-label={activeExtra.key}>
            <tbody>
              {#each map as [key, value] (key)}
                <tr>
                  <th scope="row">{key}</th>
                  <td>{value}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {:else}
          {value}
        {/if}
      {/if}
    </div>
  {/if}
{/snippet}

{#if pmp}
  <PersonaCard
    profile={pmp}
    accountId={userId}
    accountName={realName}
    accountProfile={currentProfile}
    member={roomMember}
    {powerTags}
    {onAvatarClick}
    {variant}
    below={profileBelow}
    headerAction={variant === 'popover' && canMessage ? messageAction : undefined}
    composer={variant === 'sheet' && canMessage ? composer : undefined}
  />
{:else}
  <ProfileCard
    {displayName}
    {userId}
    {avatarUrl}
    avatarLabel={i18n.t('timeline.profileAvatar', { name: displayName })}
    {onAvatarClick}
    {color}
    heroColor={currentProfile?.hero_color}
    heroBrightness={currentProfile?.hero_brightness}
    bannerUrl={currentProfile?.banner_url}
    status={userStatus?.text}
    statusEmoji={userStatus?.emoji}
    presence={presence?.presence}
    presenceLabel={presenceLabel ?? ''}
    nameColorLight={overrideColors === null
      ? null
      : (overrideColors?.light ??
        cosmetics?.colorOnLight ??
        profileColors?.name_color_light ??
        roleTag?.color)}
    nameColorDark={overrideColors === null
      ? null
      : (overrideColors?.dark ??
        cosmetics?.colorOnDark ??
        profileColors?.name_color_dark ??
        roleTag?.color)}
    meta={profileLoading ? metaPlaceholder : hasMeta ? metaRow : undefined}
    below={profileBelow}
    pronouns={pronounRow}
    children={showFailure || currentProfile?.bio ? bioPanel : undefined}
    footer={extra.length > 0 ? miscData : undefined}
    headerAction={variant === 'popover' && canMessage ? messageAction : undefined}
    composer={variant === 'sheet' && canMessage ? composer : undefined}
    insetBody
    {variant}
  />
{/if}

<MessageReportDialog
  bind:open={reportOpen}
  title={i18n.t('timeline.profileReportTitle', { name: displayName })}
  hint={i18n.t('timeline.profileReportHint')}
  onReport={(reason) => {
    const target = userId;
    void sendReport(async () => {
      await core.rpc.account.reportUser({ user_id: target, reason: reason ?? '' });
    });
  }}
/>

<ProfileOverrideDialog
  open={overrideOpen}
  {userId}
  {realName}
  {realAvatar}
  onOpenChange={(next) => (overrideOpen = next)}
/>

<UserSecurityDialog
  open={securityOpen}
  {userId}
  {displayName}
  onOpenChange={(next) => (securityOpen = next)}
/>

<ConfirmDialog
  bind:open={confirmingDirect}
  title={i18n.t('direct.confirmTitle', { name: displayName })}
  description={i18n.t('direct.confirmBody')}
  confirmLabel={i18n.t('direct.submit')}
  confirmVariant="primary"
  onConfirm={() => {
    confirmingDirect = false;
    void openDirectMessage('');
  }}
/>

<ConfirmDialog
  open={selfDemotion !== null}
  title={i18n.t('timeline.profileDemoteSelfTitle')}
  description={i18n.t('timeline.profileDemoteSelfBody')}
  confirmLabel={i18n.t('timeline.profileDemoteSelfConfirm')}
  onOpenChange={(next: boolean) => {
    if (!next) selfDemotion = null;
  }}
  onCancel={() => {
    selfDemotion = null;
  }}
  onConfirm={() => {
    if (selfDemotion !== null) setPowerLevel(selfDemotion);
  }}
/>

<DialogFrame
  open={moderationAction !== null}
  onOpenChange={(next) => {
    if (!next) cancelModeration();
  }}
  variant="verification"
  label={moderationAction === 'ban'
    ? i18n.t('timeline.profileBan')
    : i18n.t('timeline.profileKick')}
>
  {#if moderationAction}
    <div class="moderation">
      <h2>
        {moderationAction === 'ban'
          ? i18n.t('timeline.profileBanConfirm', { name: displayName })
          : i18n.t('timeline.profileKickConfirm', { name: displayName })}
      </h2>
      {#if moderationError}
        <Alert variant="critical" role="alert">{moderationError}</Alert>
      {/if}
      <FormField fieldId={moderationFieldId} label={i18n.t('timeline.deleteReason')}>
        <TextInput id={moderationFieldId} bind:value={moderationReason} autocomplete="off" />
      </FormField>
      <div class="moderation-actions">
        <Button variant="ghost" onclick={cancelModeration}>{i18n.t('common.cancel')}</Button>
        <Button variant="danger" loading={moderationBusy} onclick={confirmModeration}>
          {moderationAction === 'ban'
            ? i18n.t('timeline.profileBan')
            : i18n.t('timeline.profileKick')}
        </Button>
      </div>
    </div>
  {/if}
</DialogFrame>

<style>
  :global(.profile-mutual-menu) {
    --menu-max-height: min(32rem, 80dvh);
  }

  :global(.profile-mutual-menu .avatar-root) {
    --avatar-size: var(--avatar-size-200);
  }

  .profile-mutual-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.profile-supporter-badge) {
    align-self: center;
    margin-inline: auto calc(-1 * var(--space-100));
    order: 1;
  }

  :global(.profile-pronoun-pill) {
    --pronoun-pill-ground: var(--profile-pronoun-ground, var(--profile-panel-ground));

    color: var(--profile-text-muted);
  }

  .profile-meta-item {
    align-items: center;
    display: inline-flex;
    gap: var(--space-100);
    max-width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .profile-meta-item :global(.presence-dot) {
    margin: var(--space-100);
  }

  .profile-meta-elevated {
    color: var(--profile-ink, var(--bg-on-container));
    font-weight: var(--font-weight-medium);
  }

  .profile-meta-elevated :global(svg) {
    color: var(--profile-ink, var(--bg-on-container));
  }

  .profile-meta-aside {
    color: var(--profile-text-muted, var(--surface-var-on-container));
  }

  :global(.profile-card-actions) {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-100);
    justify-content: flex-start;
    width: 100%;
  }

  :global(.profile-card-actions > .pill-icon-only) {
    margin-left: auto;
  }

  :global(.profile-power-name) {
    flex: 1;
  }

  :global(.profile-power-level) {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    font-variant-numeric: tabular-nums;
  }

  .profile-composer {
    align-items: center;
    display: flex;
    gap: var(--space-200);
  }

  .profile-composer :global(.profile-composer-input) {
    flex: 1 1 auto;
    font-size: max(var(--font-size-small), var(--font-size-input-min));
    height: var(--control-height-small);
    min-height: 0;
    min-width: 0;
    padding-block: 0;
  }

  :global(.profile-card-sheet) .profile-composer :global(.profile-composer-input) {
    height: var(--control-height-medium);
  }

  :global(.profile-card-sheet) .profile-composer :global(.icon-button) {
    min-height: var(--control-height-medium);
    width: var(--control-height-medium);
  }

  .profile-composer-error {
    color: var(--crit-main);
    font-size: var(--font-size-small);
    margin: var(--space-200) 0 0;
  }

  :global(.btn.profile-extra),
  .profile-keys :global(.btn) {
    --button-on-container: var(--profile-ink, var(--sec-on-container));

    background: transparent;
    border: 0;
  }

  :global(.btn.profile-extra) {
    font-size: var(--font-size-small);
    line-height: var(--line-height-body);
  }

  .profile-extra-open {
    max-height: 12rem;
    overflow: auto;
  }

  .profile-key-table th,
  .profile-key-table td {
    border: var(--border-width) solid var(--profile-line, var(--surface-container-line));
    padding: var(--space-100);
  }

  .profile-key-table {
    border-collapse: collapse;
  }

  .profile-extra-json {
    font-family: var(--font-family-mono);
    font-variant-ligatures: none;
    margin: 0;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  .moderation {
    display: grid;
    gap: var(--space-300);
    width: min(27rem, calc(100vw - 2rem));
  }

  .moderation h2 {
    font-size: var(--font-size-heading);
    line-height: 1.3;
    margin: 0;
    overflow-wrap: anywhere;
  }

  .moderation-actions {
    display: flex;
    gap: var(--space-200);
    justify-content: flex-end;
  }
</style>
